import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { getCountryCallingCodeById, qualificationLevelOptions } from "@seek/profile-taxonomies";
import { useTranslations, useLanguage } from "@vocab/react";
import { Dialog, Stack, Text, Actions, Button, Box, TextLinkButton, IconChevron, Inline, Columns, Column, Heading, ButtonIcon, IconEdit, Disclosure, IconArrow, Card, Badge, Divider, Tag } from "braid-design-system";
import { useCallback, useState, useEffect, useRef } from "react";
import { translations } from "./components/CountryCallingCodeDialog/.vocab/index.mjs";
import { CredentialStatus, compatibility, validateUnconfirmedQualification, formatters, validateUnconfirmedRole } from "@seek/ca-myprofile-utils";
import { VerificationLogo, EducationLogo } from "@seek/certsy-ui";
import { CertsyVerifiedIndicator, ProfileCardDescription, useDialogHistoryManager, DefinitionList, CertsyDialog } from "@seek/profile-base-ui";
import { translations as translations$1 } from "./components/ReadMore/.vocab/index.mjs";
import { translations as translations$2 } from "./components/ProfileItemContent/.vocab/index.mjs";
import { useInView } from "react-intersection-observer";
import { translations as translations$3 } from "./components/Licence/.vocab/index.mjs";
import { translations as translations$4 } from "./components/Qualification/.vocab/index.mjs";
import { translations as translations$5 } from "./components/Role/.vocab/index.mjs";
const formatPhoneNumber = (countryCallingCode, phoneNumber) => `+${countryCallingCode} ${phoneNumber.replace(/\s/g, " ")}`;
const CountryCallingCodeDialog = ({
  countryCallingCode: countryCallingCodeValue,
  phoneNumber,
  onSave,
  onClose,
  open,
  width = "xsmall"
}) => {
  const { t } = useTranslations(translations);
  const onSaveClick = useCallback(
    (event) => {
      onSave(event);
      onClose();
    },
    [onSave, onClose]
  );
  const [countryCallingCode, setCountryCallingCode] = useState("");
  useEffect(() => {
    let shouldSetCountryCallingCode = true;
    getCountryCallingCodeById(countryCallingCodeValue.id).then(
      (callingCode) => {
        if (shouldSetCountryCallingCode) {
          setCountryCallingCode(callingCode);
        }
      }
    );
    return () => {
      shouldSetCountryCallingCode = false;
    };
  }, [countryCallingCodeValue.id]);
  return /* @__PURE__ */ jsx(
    Dialog,
    {
      id: "countryCallingCodeDialog",
      width,
      onClose,
      closeLabel: t("Close"),
      open,
      title: t("Country code now included in phone number"),
      children: /* @__PURE__ */ jsxs(Stack, { space: "large", align: "center", children: [
        /* @__PURE__ */ jsx(Text, { align: "center", children: t("Your phone number will be saved as...", {
          formattedPhoneNumber: formatPhoneNumber(
            countryCallingCode,
            phoneNumber
          )
        }) }),
        /* @__PURE__ */ jsxs(Actions, { children: [
          /* @__PURE__ */ jsx(
            Button,
            {
              tone: "formAccent",
              onClick: onSaveClick,
              data: { automation: "country-calling-code-dialog-save-button" },
              children: t("Save")
            }
          ),
          /* @__PURE__ */ jsx(
            Button,
            {
              tone: "formAccent",
              variant: "ghost",
              data: { automation: "country-calling-code-dialog-cancel-button" },
              onClick: onClose,
              children: t("Cancel")
            }
          )
        ] })
      ] })
    }
  );
};
const ReadMore = ({
  children,
  linesToPreview = 2,
  moreLabel,
  lessLabel,
  onMoreButtonVisibilityChange,
  onShowMore,
  showFullTextOnly
}) => {
  const { t } = useTranslations(translations$1);
  const fullTextRef = useRef(null);
  const visibleTextRef = useRef(null);
  const [showFullText, setShowFullText] = useState(
    Boolean(showFullTextOnly) === true
  );
  const [showButton, setShowButton] = useState(false);
  const translatedMoreLabel = moreLabel || t("More");
  const translatedLessLabel = lessLabel || t("Less");
  useEffect(() => {
    var _a;
    const calculateIfTextTooLong = (fullEl, visibleTextEl) => {
      const fullTextHeight = fullEl.scrollHeight;
      const visibleTextHeight = visibleTextEl.scrollHeight;
      const isTooLong = fullTextHeight > visibleTextHeight;
      setShowButton(isTooLong);
      if (onMoreButtonVisibilityChange && isTooLong !== showButton) {
        onMoreButtonVisibilityChange(showButton);
      }
    };
    if (fullTextRef.current && visibleTextRef.current) {
      calculateIfTextTooLong(fullTextRef.current, visibleTextRef.current);
      (_a = document.fonts) == null ? void 0 : _a.ready.then(() => {
        if (fullTextRef.current && visibleTextRef.current) {
          calculateIfTextTooLong(fullTextRef.current, visibleTextRef.current);
        }
      });
    }
  }, [onMoreButtonVisibilityChange, showButton, children]);
  const handleShowMore = useCallback(() => {
    setShowFullText(!showFullText);
    if (onShowMore) {
      onShowMore(!showFullText);
    }
  }, [onShowMore, showFullText]);
  return /* @__PURE__ */ jsxs(Box, { children: [
    /* @__PURE__ */ jsxs(Box, { position: "relative", children: [
      /* @__PURE__ */ jsx(
        Box,
        {
          ref: fullTextRef,
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          overflow: "hidden",
          opacity: 0,
          children: /* @__PURE__ */ jsx(Text, { children })
        }
      ),
      /* @__PURE__ */ jsx(Box, { ref: visibleTextRef, "aria-hidden": true, children: /* @__PURE__ */ jsx(Text, { maxLines: showFullText ? void 0 : linesToPreview, children }) })
    ] }),
    showButton && /* @__PURE__ */ jsx(Box, { marginTop: "small", "aria-hidden": true, children: /* @__PURE__ */ jsx(Text, { children: /* @__PURE__ */ jsx(
      TextLinkButton,
      {
        onClick: handleShowMore,
        hitArea: "large",
        weight: "weak",
        icon: /* @__PURE__ */ jsx(
          IconChevron,
          {
            direction: showFullText ? "up" : "down",
            alignY: "lowercase"
          }
        ),
        iconPosition: "trailing",
        children: showFullText ? translatedLessLabel : translatedMoreLabel
      }
    ) }) })
  ] });
};
ReadMore.displayName = "ReadMore";
const UnconfirmedProfileItemContent = ({
  children,
  trackUnconfirmedImpressionFn,
  type
}) => {
  const { ref, inView } = useInView({
    threshold: 1
    // Fully in view
  });
  useEffect(() => {
    if (inView) {
      trackUnconfirmedImpressionFn == null ? void 0 : trackUnconfirmedImpressionFn();
    }
  }, [inView, trackUnconfirmedImpressionFn]);
  return /* @__PURE__ */ jsx(Box, { "data-automation": `read-${type}-unconfirmed`, ref, children });
};
const ProfileItemContent = ({
  type,
  id,
  title,
  titleLink,
  titleLinkClick,
  subtitle,
  subtitleLink,
  subtitleLinkClick,
  date,
  dateTone = "secondary",
  longText,
  additionalContent,
  addUnconfirmed,
  rejectUnconfirmed,
  editFn,
  trackUnconfirmedImpressionFn,
  confirmed,
  isValid,
  badges,
  warningText,
  replaceUrlTextWithLinks,
  displayVariant,
  isVerified,
  showVerifiedText = false
}) => {
  const { t } = useTranslations(translations$2);
  const [readMore, setReadMore] = useState({
    readMoreClicked: false,
    readMoreVisible: false
  });
  const addToProfileClick = () => {
    if (addUnconfirmed) {
      const contentRead = readMore.readMoreVisible === false || readMore.readMoreVisible && readMore.readMoreClicked;
      addUnconfirmed(id, contentRead);
    }
  };
  const rejectUnconfirmedClick = () => {
    rejectUnconfirmed == null ? void 0 : rejectUnconfirmed(id);
  };
  const editFnClick = () => {
    editFn == null ? void 0 : editFn(id, confirmed);
  };
  const moreButtonVisibilityChange = (readMoreVisible) => setReadMore({ ...readMore, readMoreVisible });
  const readMoreClick = (readMoreVisible) => setReadMore({
    readMoreClicked: true,
    readMoreVisible
  });
  const itemContents = /* @__PURE__ */ jsx("div", { children: /* @__PURE__ */ jsxs(Stack, { space: "medium", children: [
    /* @__PURE__ */ jsxs(Stack, { space: "medium", children: [
      /* @__PURE__ */ jsxs(Stack, { space: "medium", children: [
        (badges == null ? void 0 : badges.length) ? /* @__PURE__ */ jsx(Inline, { space: "xxsmall", children: badges }) : null,
        /* @__PURE__ */ jsxs(Stack, { space: "small", children: [
          /* @__PURE__ */ jsxs(Columns, { space: "gutter", children: [
            /* @__PURE__ */ jsx(Column, { children: /* @__PURE__ */ jsxs(Inline, { space: "xxsmall", alignY: "bottom", children: [
              /* @__PURE__ */ jsx(Heading, { level: "4", children: /* @__PURE__ */ jsx("span", { style: { overflowWrap: "break-word" }, children: title }) }),
              isVerified ? /* @__PURE__ */ jsx(
                CertsyVerifiedIndicator,
                {
                  idSuffix: id,
                  showVerifiedText
                }
              ) : null
            ] }) }),
            editFn ? /* @__PURE__ */ jsx(Column, { width: "content", children: /* @__PURE__ */ jsx(Box, { paddingLeft: "gutter", children: /* @__PURE__ */ jsx(
              ButtonIcon,
              {
                id: `edit-${type}-${id}`,
                onClick: editFnClick,
                icon: /* @__PURE__ */ jsx(IconEdit, {}),
                variant: "transparent",
                label: t("Edit title", { title }),
                data: { automation: `edit-${type}-${id}` }
              }
            ) }) }) : null
          ] }),
          subtitle ? /* @__PURE__ */ jsxs(Inline, { space: "xsmall", alignY: "bottom", children: [
            /* @__PURE__ */ jsx(Text, { children: subtitle }),
            subtitleLink && confirmed ? /* @__PURE__ */ jsx(Text, { size: "small", children: /* @__PURE__ */ jsx(
              TextLinkButton,
              {
                onClick: subtitleLinkClick,
                hitArea: "large",
                weight: "weak",
                children: subtitleLink
              }
            ) }) : null
          ] }) : null
        ] })
      ] }),
      date ? /* @__PURE__ */ jsx(Text, { tone: dateTone, component: "time", children: date }) : null,
      warningText ? /* @__PURE__ */ jsx(Text, { tone: "critical", children: warningText }) : null,
      longText ? /* @__PURE__ */ jsx(
        ReadMore,
        {
          lessLabel: t("Less"),
          moreLabel: t("More"),
          linesToPreview: 2,
          onShowMore: readMoreClick,
          onMoreButtonVisibilityChange: moreButtonVisibilityChange,
          showFullTextOnly: displayVariant === "full-text-no-divider",
          children: typeof longText === "string" ? /* @__PURE__ */ jsx(
            ProfileCardDescription,
            {
              text: longText,
              linksAreKeyboardAccessible: readMore.readMoreVisible,
              replaceUrlTextWithLinks
            }
          ) : longText
        }
      ) : null,
      additionalContent ? /* @__PURE__ */ jsx(
        Disclosure,
        {
          id: "additionalContentDislosure",
          weight: "weak",
          collapseLabel: t("Less"),
          expandLabel: t("More"),
          children: typeof additionalContent === "string" ? /* @__PURE__ */ jsx(
            ProfileCardDescription,
            {
              text: additionalContent,
              linksAreKeyboardAccessible: readMore.readMoreVisible,
              replaceUrlTextWithLinks
            }
          ) : additionalContent
        }
      ) : null
    ] }),
    titleLink ? /* @__PURE__ */ jsx(Actions, { children: /* @__PURE__ */ jsx(
      Button,
      {
        tone: "formAccent",
        variant: "solid",
        icon: /* @__PURE__ */ jsx(IconArrow, { direction: "right" }),
        iconPosition: "trailing",
        onClick: titleLinkClick,
        children: titleLink
      }
    ) }) : null,
    !confirmed && /* @__PURE__ */ jsxs(Actions, { children: [
      /* @__PURE__ */ jsx(
        Button,
        {
          id,
          tone: "formAccent",
          data: { automation: `add-to-profile-${id}` },
          onClick: isValid ? addToProfileClick : editFnClick,
          children: t("Add to Profile")
        }
      ),
      /* @__PURE__ */ jsx(
        Button,
        {
          tone: "formAccent",
          variant: "ghost",
          data: { automation: `reject-${type}-${id}` },
          onClick: rejectUnconfirmedClick,
          children: t("Do not include")
        }
      )
    ] })
  ] }) });
  const profileItemContents = () => {
    switch (displayVariant) {
      case "card": {
        return /* @__PURE__ */ jsx(Card, { children: itemContents });
      }
      case "full-text-no-divider": {
        return /* @__PURE__ */ jsx(Box, { paddingY: "small", children: itemContents });
      }
      default: {
        return itemContents;
      }
    }
  };
  return confirmed ? /* @__PURE__ */ jsx(Box, { "data-automation": `read-${type}`, children: profileItemContents() }) : /* @__PURE__ */ jsx(
    UnconfirmedProfileItemContent,
    {
      trackUnconfirmedImpressionFn,
      type,
      children: profileItemContents()
    }
  );
};
const Licence = ({
  id,
  name,
  issuingOrganisation = "",
  description = "",
  editLicence,
  onVerifyClick,
  onReVerifyClick,
  onCertsyClick,
  showCertsyFunctionality = true,
  credential,
  status,
  verificationUrl,
  verificationMessage,
  formattedDate,
  replaceUrlTextWithLinks,
  isVerified: isLicenceVerified = false,
  displayVariant,
  showVerifiedText,
  showExpiryStatus
}) => {
  const { t } = useTranslations(translations$3);
  const isVerifiable = Boolean(verificationUrl) && showCertsyFunctionality;
  const isVerified = isLicenceVerified || Boolean(credential);
  const expiryStatus = credential == null ? void 0 : credential.expiryStatus;
  const isNew = status === CredentialStatus.New;
  const credentialInfo = credential == null ? void 0 : credential.credentialInfo;
  const [displayDialog, setDisplayDialog] = useState(false);
  const { openDialog, closeDialog } = useDialogHistoryManager(setDisplayDialog);
  const titleLinkClick = useCallback(() => {
    if (isVerifiable) {
      if (isVerified && onReVerifyClick) {
        onReVerifyClick();
      } else if (onVerifyClick) onVerifyClick();
      openDialog();
    }
  }, [isVerifiable, isVerified, onReVerifyClick, onVerifyClick, openDialog]);
  const onClose = useCallback(() => {
    closeDialog();
  }, [closeDialog]);
  const hasVerifiedCredentials = isVerified && credentialInfo;
  const verifiedCredentialsAndDescription = hasVerifiedCredentials ? /* @__PURE__ */ jsxs(Stack, { space: "large", children: [
    /* @__PURE__ */ jsx(DefinitionList, { data: credentialInfo }),
    Boolean(description) && /* @__PURE__ */ jsxs(Stack, { space: "small", children: [
      /* @__PURE__ */ jsx(Text, { tone: "secondary", children: t("Your description") }),
      /* @__PURE__ */ jsx(Text, { children: /* @__PURE__ */ jsx(
        ProfileCardDescription,
        {
          text: description,
          replaceUrlTextWithLinks
        }
      ) })
    ] })
  ] }) : void 0;
  const getBadges = (isVerifed, isNewInput, expiryStatusInput) => {
    const badges = [];
    if (showExpiryStatus && expiryStatusInput === "EXPIRED") {
      badges.push(
        /* @__PURE__ */ jsx(Badge, { tone: "critical", bleedY: true, children: t("Expired") }, "expiredBadge")
      );
      badges.push(
        /* @__PURE__ */ jsx(Badge, { tone: "neutral", bleedY: true, children: t("Only visible to you") }, "onlyVisibleToYouBadge")
      );
    } else if (showExpiryStatus && expiryStatusInput === "EXPIRING_SOON") {
      badges.push(
        /* @__PURE__ */ jsx(Badge, { tone: "caution", bleedY: true, children: t("Expiring soon") }, "expiringSoonBadge")
      );
    } else if (isVerifed && isNewInput) {
      badges.push(
        /* @__PURE__ */ jsx(Badge, { tone: "positive", bleedY: true, children: t("New") }, "newBadge")
      );
    }
    return badges;
  };
  const licenceProps = {
    id,
    type: "licence",
    title: name.text,
    isVerified,
    ...isVerifiable ? {
      titleLink: isVerified ? t("Re-verify") : t("Verify"),
      titleLinkClick
    } : {},
    subtitle: issuingOrganisation,
    date: formattedDate,
    longText: !hasVerifiedCredentials ? description : void 0,
    additionalContent: verifiedCredentialsAndDescription,
    editFn: editLicence,
    confirmed: true,
    isValid: true,
    badges: getBadges(isVerified, isNew, expiryStatus),
    replaceUrlTextWithLinks,
    displayVariant,
    showVerifiedText
  };
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(
      ProfileItemContent,
      {
        ...licenceProps
      }
    ),
    /* @__PURE__ */ jsx(
      CertsyDialog,
      {
        bodyText: t("Help your application stand out..."),
        certsyButtonText: t("Verify on SEEK Pass"),
        certsyUrl: verificationUrl || "",
        onCertsyClick,
        title: verificationMessage,
        open: displayDialog,
        onClose,
        seekPassLogo: /* @__PURE__ */ jsx(VerificationLogo, {})
      }
    )
  ] });
};
Licence.displayName = "Licence";
const Licences = (props) => {
  const {
    licences,
    displayVariant,
    showVerifiedText,
    showExpiryStatus = false,
    ...restProps
  } = props;
  const showDividers = displayVariant !== "card" && displayVariant !== "full-text-no-divider";
  return /* @__PURE__ */ jsx(Stack, { component: "ul", space: displayVariant === "card" ? "small" : "large", children: licences.map((licence, i) => /* @__PURE__ */ jsxs(Fragment, { children: [
    showDividers && i > 0 ? /* @__PURE__ */ jsx(Divider, {}) : null,
    /* @__PURE__ */ jsx(Box, { component: "li", children: /* @__PURE__ */ jsx(
      Licence,
      {
        ...licence,
        ...restProps,
        displayVariant,
        showVerifiedText,
        showExpiryStatus
      },
      i
    ) })
  ] })) });
};
const ProfileSkills = ({ skills }) => /* @__PURE__ */ jsx(Box, { "data-automation": "skills-items", children: /* @__PURE__ */ jsx(Inline, { space: "small", component: "ul", children: [...skills].reverse().map((skill, i) => /* @__PURE__ */ jsx(Box, { component: "li", children: /* @__PURE__ */ jsx(Tag, { children: skill }) }, i)) }) });
const useCompatibleLanguage = () => {
  const { language: vocabLanguage } = useLanguage();
  const language = compatibility.getCompatibleLanguageFromLocaleString(vocabLanguage);
  return language;
};
const useGetFormattedCompletion = ({
  completed,
  completionDate
}) => {
  const [formattedCompletion, setFormattedCompletion] = useState("");
  const { locale } = useLanguage();
  const language = useCompatibleLanguage();
  useEffect(() => {
    let shouldSetFormattedCompletion = true;
    formatters.formatEducationCompletion(
      { completed, completionDate },
      language,
      locale
    ).then((result) => {
      if (shouldSetFormattedCompletion) {
        setFormattedCompletion(result);
      }
    });
    return () => {
      shouldSetFormattedCompletion = false;
    };
  }, [completed, completionDate, language, locale]);
  return formattedCompletion;
};
const Qualification = ({
  id,
  level2,
  name: { text: qualificationName, ontologyId: qualificationOntologyId },
  institute: {
    text: instituteName,
    ontologyId: qualificationInstituteOntologyId
  },
  completionDate,
  completed,
  formattedCompletion: formattedCompletionProp,
  highlights = "",
  confirmed = true,
  addUnconfirmedQualification,
  rejectUnconfirmedQualification,
  editQualification,
  trackUnconfirmedQualificationImpression,
  tracking,
  onVerifyClick,
  onCertsyClick,
  showCertsyFunctionality = true,
  credential,
  status,
  verificationUrl,
  zone = "anz-1",
  replaceUrlTextWithLinks,
  isVerified: isQualificationVerified = false,
  displayVariant,
  showVerifiedText
}) => {
  const { t } = useTranslations(translations$4);
  const [levelOptions, setLevelOptions] = useState([]);
  const language = useCompatibleLanguage();
  useEffect(() => {
    let shouldSetLevelOptions = true;
    qualificationLevelOptions(void 0, language).then((options) => {
      if (shouldSetLevelOptions) {
        setLevelOptions(options);
      }
    });
    return () => {
      shouldSetLevelOptions = false;
    };
  }, [language]);
  const fallbackFormattedCompletion = useGetFormattedCompletion({
    completed,
    completionDate
  });
  const formattedCompletion = formattedCompletionProp ? formattedCompletionProp : fallbackFormattedCompletion;
  const trackImpressionFunction = () => {
    if (trackUnconfirmedQualificationImpression) {
      trackUnconfirmedQualificationImpression({
        id,
        name: { text: qualificationName, ontologyId: qualificationOntologyId },
        institute: {
          text: instituteName,
          ontologyId: qualificationInstituteOntologyId
        },
        completed,
        completionDate,
        highlights,
        tracking
      });
    }
  };
  const isValid = validateUnconfirmedQualification(
    {
      qualificationName,
      instituteName,
      ontologyId: qualificationOntologyId,
      highlights,
      level2
    },
    zone
  );
  const isVerifiable = Boolean(verificationUrl) && showCertsyFunctionality;
  const isVerified = isQualificationVerified || Boolean(credential);
  const isNew = status === CredentialStatus.New;
  const credentialInfo = credential == null ? void 0 : credential.credentialInfo;
  const [displayDialog, setDisplayDialog] = useState(false);
  const { openDialog, closeDialog } = useDialogHistoryManager(setDisplayDialog);
  const titleLinkClick = useCallback(() => {
    if (verificationUrl) {
      onVerifyClick == null ? void 0 : onVerifyClick();
      openDialog();
    }
  }, [onVerifyClick, openDialog, verificationUrl]);
  const onClose = useCallback(() => {
    closeDialog();
  }, [closeDialog]);
  const hasVerifiedCredentials = isVerified && credentialInfo;
  const verifiedCredentialsAndHighlights = hasVerifiedCredentials ? /* @__PURE__ */ jsxs(Stack, { space: "large", children: [
    /* @__PURE__ */ jsx(DefinitionList, { data: credentialInfo }),
    Boolean(highlights) && /* @__PURE__ */ jsxs(Stack, { space: "small", children: [
      /* @__PURE__ */ jsx(Text, { tone: "secondary", children: t("Your highlights") }),
      /* @__PURE__ */ jsx(Text, { children: /* @__PURE__ */ jsx(
        ProfileCardDescription,
        {
          text: highlights,
          replaceUrlTextWithLinks
        }
      ) })
    ] })
  ] }) : void 0;
  const foundInResumeBadge = /* @__PURE__ */ jsx(Badge, { tone: "promote", bleedY: true, children: t("Found in resumé") }, "1");
  const newBadge = /* @__PURE__ */ jsx(Badge, { tone: "positive", bleedY: true, children: t("New") }, "0");
  const badges = [];
  if (!confirmed) {
    badges.push(foundInResumeBadge);
  }
  if (isVerified && isNew) {
    badges.push(newBadge);
  }
  const getTitle = () => {
    if (level2 && level2 !== "NA") {
      const levelTaxonomyItem = levelOptions.find(
        (taxonomyItem) => taxonomyItem.id === level2
      );
      if (levelTaxonomyItem) {
        return t("qualification level in qualification name", {
          qualificationLevel: levelTaxonomyItem.description,
          qualificationName
        });
      }
    }
    return qualificationName;
  };
  const qualificationProps = {
    id,
    type: "qualification",
    isValid,
    title: getTitle(),
    isVerified,
    ...!isVerified && isVerifiable ? {
      titleLink: t("Verify"),
      titleLinkClick
    } : {},
    subtitle: isVerified && credential ? null : instituteName,
    date: isVerified && credential ? null : formattedCompletion,
    longText: !hasVerifiedCredentials ? highlights : void 0,
    additionalContent: verifiedCredentialsAndHighlights,
    confirmed,
    addUnconfirmed: addUnconfirmedQualification,
    rejectUnconfirmed: rejectUnconfirmedQualification,
    editFn: editQualification,
    trackUnconfirmedImpressionFn: trackImpressionFunction,
    badges,
    displayVariant,
    showVerifiedText
  };
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(
      ProfileItemContent,
      {
        ...qualificationProps
      }
    ),
    /* @__PURE__ */ jsx(
      CertsyDialog,
      {
        bodyText: t("Help your application stand out..."),
        certsyButtonText: t("Verify on SEEK Pass"),
        certsyUrl: verificationUrl || "",
        onCertsyClick,
        title: t("Verify your education"),
        open: displayDialog,
        onClose,
        seekPassLogo: /* @__PURE__ */ jsx(EducationLogo, {})
      }
    )
  ] });
};
const Qualifications = ({
  qualifications,
  displayVariant,
  showVerifiedText = false,
  ...restProps
}) => {
  const showDividers = displayVariant !== "card" && displayVariant !== "full-text-no-divider";
  return /* @__PURE__ */ jsx(Stack, { component: "ul", space: displayVariant === "card" ? "small" : "large", children: qualifications.map((qualification, i) => /* @__PURE__ */ jsxs(Fragment, { children: [
    showDividers && i > 0 ? /* @__PURE__ */ jsx(Divider, {}) : null,
    /* @__PURE__ */ jsx(Box, { component: "li", children: /* @__PURE__ */ jsx(
      Qualification,
      {
        ...qualification,
        ...restProps,
        displayVariant,
        showVerifiedText
      },
      i
    ) })
  ] })) });
};
const useGetFormattedRoleDate = (from, to) => {
  const [formattedRoleDate, setFormattedRoleDate] = useState("");
  const language = useCompatibleLanguage();
  useEffect(() => {
    let shouldSetFormattedRoleDate = true;
    formatters.formatRoleDate({ from, to }, language).then((result) => {
      if (shouldSetFormattedRoleDate) {
        setFormattedRoleDate(result);
      }
    });
    return () => {
      shouldSetFormattedRoleDate = false;
    };
  }, [from, language, to]);
  return formattedRoleDate;
};
const Role = ({
  id,
  company: { text: company, ontologyId: companyOntologyId },
  title: { text: title, ontologyId: titleOntologyId },
  from,
  to: toRaw,
  achievements = "",
  showMissingAchievementsText = false,
  confirmed = true,
  showReviewLink = false,
  onReviewClick,
  addUnconfirmedRole,
  rejectUnconfirmedRole,
  trackUnconfirmedRoleImpression,
  editRole,
  seniority,
  tracking,
  replaceUrlTextWithLinks,
  displayVariant
}) => {
  const { t } = useTranslations(translations$5);
  const to = toRaw && JSON.stringify(toRaw) !== "{}" ? toRaw : void 0;
  const formattedRoleDate = useGetFormattedRoleDate(from, to);
  const trackImpressionFunction = () => {
    if (trackUnconfirmedRoleImpression) {
      trackUnconfirmedRoleImpression({
        id,
        company: { text: company, ontologyId: companyOntologyId },
        title: { text: title, ontologyId: titleOntologyId },
        from,
        to: toRaw,
        achievements,
        seniority,
        tracking
      });
    }
  };
  const isValid = validateUnconfirmedRole({
    title,
    company,
    from,
    to,
    achievements
  });
  const reviewClick = () => {
    const url = `/companies/write-a-review?tracking=overview-personalised&cn=${encodeURIComponent(
      company
    )}`;
    if (onReviewClick) {
      onReviewClick({ id, companyName: company, url });
    } else {
      window.open(url);
    }
  };
  const badges = [
    /* @__PURE__ */ jsx(Badge, { tone: "promote", bleedY: true, children: t("Found in resumé") }, "1")
  ];
  const subtitleLinkProps = showReviewLink ? {
    subtitleLink: t("Review this company"),
    subtitleLinkClick: reviewClick
  } : {};
  const roleProps = {
    id,
    type: "role",
    date: formattedRoleDate,
    isValid,
    subtitle: company,
    title,
    longText: achievements || "",
    reviewClick,
    confirmed,
    addUnconfirmed: addUnconfirmedRole,
    rejectUnconfirmed: rejectUnconfirmedRole,
    editFn: editRole,
    warningText: showMissingAchievementsText ? t("This role has no description.") : void 0,
    trackUnconfirmedImpressionFn: trackImpressionFunction,
    badges: !confirmed ? badges : void 0,
    ...subtitleLinkProps,
    replaceUrlTextWithLinks,
    displayVariant
  };
  return /* @__PURE__ */ jsx(ProfileItemContent, { ...roleProps });
};
const Roles = (props) => {
  const { roles, displayVariant, ...restProps } = props;
  const showDividers = displayVariant !== "card" && displayVariant !== "full-text-no-divider";
  return /* @__PURE__ */ jsx(Stack, { component: "ul", space: displayVariant === "card" ? "small" : "large", children: roles.map((role, i) => /* @__PURE__ */ jsxs(Fragment, { children: [
    showDividers && i > 0 ? /* @__PURE__ */ jsx(Divider, {}) : null,
    /* @__PURE__ */ jsx(Box, { component: "li", children: /* @__PURE__ */ jsx(
      Role,
      {
        ...role,
        ...restProps,
        displayVariant
      },
      i
    ) })
  ] })) });
};
export {
  CountryCallingCodeDialog,
  Licence,
  Licences,
  ProfileItemContent,
  ProfileSkills,
  Qualification,
  Qualifications,
  ReadMore,
  Role,
  Roles
};
